import React, { useEffect, useRef, useState } from "react";
import {
  CardElement,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

// Custom styling can be passed to options when creating an Element.
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

export const Form = (props) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const stripe = useStripe();
  const elements = useElements();
  const buttonText = props.i18n.button_text;
  const loadingText = props.i18n.loading_text;
  const navigate = props.navigate || ((url) => (window.location = url));

  // Latest-ref (review): the paymentmethod listener below is attached once
  // per paymentRequest but must see the current props and finish, not the
  // ones captured on the render that ran the effect — the standard ref fix
  // for stale closures in long-lived event handlers.
  const latestRef = useRef({ props, finish: null });
  useEffect(() => {
    latestRef.current = { props, finish };
  });

  // GR-1185/GR-1186: Apple Pay / Google Pay via Stripe's Payment Request
  // Button. The wallet hands us a PaymentMethod (pm_...) exactly like
  // createPaymentMethod does, so it flows through the same backend path.
  useEffect(() => {
    const wallet = props.walletPayment;
    if (!stripe || !wallet) return undefined;

    let cancelled = false;
    const pr = stripe.paymentRequest({
      country: wallet.country,
      currency: wallet.currency,
      total: { label: wallet.label, amount: wallet.amount },
      requestPayerName: false,
      requestPayerEmail: false,
      // Only the wallets enabled on the gateway — never Link or the
      // browser's saved cards, which the card form already covers.
      disableWallets: [
        "link",
        "browserCard",
        ...(wallet.applePay ? [] : ["applePay"]),
        ...(wallet.googlePay ? [] : ["googlePay"]),
      ],
    });

    pr.on("paymentmethod", async (ev) => {
      // The native sheet stays open until complete() is called — never let
      // an exception (e.g. a network failure) leave it hanging, and never
      // call complete() twice.
      let completed = false;
      const complete = (status) => {
        if (completed) return;
        completed = true;
        ev.complete(status);
      };

      setLoading(true);
      setError(null);
      try {
        const response = await latestRef.current.props.onSubmitPayment({
          payment_method_id: ev.paymentMethod.id,
        });
        if (response.requires_action) {
          // Close the wallet sheet before the 3DS challenge takes over.
          complete("success");
          const confirmation = await stripe.confirmCardPayment(
            response.client_secret
          );
          if (confirmation.error) {
            setError(confirmation.error.message);
            setLoading(false);
            return;
          }
          const completion = await latestRef.current.props.onSubmitPayment({
            payment_intent_id: confirmation.paymentIntent.id,
          });
          latestRef.current.finish(completion);
        } else {
          complete(response.success ? "success" : "fail");
          latestRef.current.finish(response);
        }
      } catch (err) {
        // Keep the original error visible in the console (the !3974 / GR-2529
        // convention): this catch must never silently repaint a programming
        // error as a payment error.
        console.error(err);
        complete("fail");
        setError(latestRef.current.props.i18n.error_text);
        setLoading(false);
      }
    });

    // Fall back silently to the card form when no wallet is available.
    pr.canMakePayment().then((result) => {
      if (!cancelled && result && (result.applePay || result.googlePay)) {
        setPaymentRequest(pr);
      }
    });

    // Cleanup: a superseded paymentRequest must never surface its button
    // after the effect re-runs or the component unmounts.
    return () => {
      cancelled = true;
    };
  }, [stripe, props.walletPayment]);

  // Handle real-time validation errors from the card Element.
  const handleChange = (event) => {
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  // Handle form submission (GR-1188, Payment Intents flow):
  // 1. Tokenise the card as a PaymentMethod (pm_...)
  // 2. Submit it — the backend creates and confirms a PaymentIntent
  // 3. If Stripe requires a customer action, complete it with
  //    confirmCardPayment and re-submit the confirmed intent (pi_...)
  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const card = elements.getElement(CardElement);
      const result = await stripe.createPaymentMethod({ type: "card", card });
      if (result.error) {
        // Inform the user if there was an error.
        setError(result.error.message);
        setLoading(false);
        return;
      }
      setError(null);
      const response = await props.onSubmitPayment({
        payment_method_id: result.paymentMethod.id,
      });
      if (response.requires_action) {
        const confirmation = await stripe.confirmCardPayment(
          response.client_secret,
          { payment_method: result.paymentMethod.id }
        );
        if (confirmation.error) {
          setError(confirmation.error.message);
          setLoading(false);
          return;
        }
        const completion = await props.onSubmitPayment({
          payment_intent_id: confirmation.paymentIntent.id,
        });
        finish(completion);
      } else {
        finish(response);
      }
    } catch (err) {
      // GR-2529: a thrown failure (network drop, interrupted challenge)
      // must not leave the button stuck on the loading state — surface a
      // retryable error instead. Keep the original error visible in the
      // console: this catch must never silently repaint a programming
      // error as a payment error.
      console.error(err);
      setError(props.i18n.error_text);
      setLoading(false);
    }
  };

  // A response without a location is a failed or malformed round trip —
  // surface an error instead of navigating to undefined.
  const finish = (response) => {
    if (response.location) {
      navigate(response.location);
    } else {
      setError(props.i18n.error_text);
    }
    setLoading(false);
  };

  // GR-2528: Klarna is redirect-based — the backend creates an unconfirmed
  // intent and confirmKlarnaPayment sends the customer to Klarna. On return
  // the backend's klarna_return endpoint completes the payment, so success
  // never comes back through here; reaching past confirmKlarnaPayment means
  // it failed to redirect.
  const handleKlarnaSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await props.onSubmitPayment({
        payment_method_type: "klarna",
      });
      if (response.requires_redirect) {
        const result = await stripe.confirmKlarnaPayment(
          response.client_secret,
          { return_url: props.klarnaPayment.returnUrl }
        );
        if (result && result.error) {
          setError(result.error.message);
        }
        setLoading(false);
      } else {
        // Backend declined to start the Klarna flow (e.g. not eligible) —
        // it responds like any failed payment, with a location to navigate to.
        finish(response);
      }
    } catch (e) {
      // Both buttons share `loading`, so an escaping rejection here freezes
      // the card button too — the whole form sticks on "Processing..." with
      // nothing shown. Never leave the customer with no way to retry.
      setError(props.i18n.error_text);
      setLoading(false);
    }
  };

  return (
    <form className="stripe-gateway" onSubmit={handleSubmit}>
      {paymentRequest && (
        <div className="wallet-payment">
          <PaymentRequestButtonElement options={{ paymentRequest }} />
          <div className="wallet-divider">{props.i18n.wallet_divider}</div>
        </div>
      )}
      <div className="form-row">
        <label htmlFor="card-element">{props.i18n.title}</label>
        <CardElement
          id="card-element"
          options={CARD_ELEMENT_OPTIONS}
          onChange={handleChange}
        />
        <div className="card-errors" role="alert">
          {error}
        </div>
      </div>
      <button disabled={loading} className="button full action" type="submit">
        {loading ? loadingText : buttonText}
      </button>
      {props.klarnaPayment && (
        <div className="klarna-payment">
          <div className="klarna-divider">{props.i18n.klarna_divider}</div>
          <button
            disabled={loading}
            className="button full"
            type="button"
            onClick={handleKlarnaSubmit}
          >
            {loading ? loadingText : props.i18n.klarna_button_text}
          </button>
        </div>
      )}
    </form>
  );
};
